import { useCallback } from 'react';
import { batch } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  addBreakdownGroup,
  addCohortRow,
  addEventValuesAction,
  addExpandEventAction,
  addFoldEventAction,
  addMergeEventAction,
  addNewEventAction,
  addNewRowAction,
  addSimpleCohort,
  dataTableMeasureSetNamedMeasure,
  populateNamedBuilderCohort,
  populateNamedCohortReference,
  queryMeasureSetNamedMeasure,
  removeErrorAction,
  RowType,
  setBreakdownGroupField,
  setBuilderFunctionAction,
  setCohortRowSimpleTypeKey,
  setEventPropertyAction,
  setExpandEventAction,
  setFilterAction,
  setRowPropAction,
  setSignificanceBreakdownField,
  setSignificanceControlGroup,
  setSignificanceMeasureMetric,
  setSignificanceVariant,
} from '../../redux/slices/oldBuilder';
import {
  IBasicEventValueFieldsFragment,
  ICompoundMeasureFieldsFragment,
  IFieldFieldsFragment,
  IFilterFieldsFragment,
  ILogicalOperator,
  ISimpleMeasureFieldsFragment,
  IVirtualEventFieldsFragment,
  LogicalOperatorKey,
  SignificanceModeKey,
  SimpleCohortTypeKey,
} from '../../utils/apollo/graphqlSchema';
import { useEntityLoader } from '../Builder/EntitySelection/useEntityLoader';
import {
  checkExistingEvent,
  checkExistingEventsEntityType,
  createDictionaryEntityDropzoneId,
  createNewBasicFilter,
  getDndMeasureFunction,
  updateFilterWithCondition,
} from './utils';
import { isNil } from 'lodash';
import { IEntityNode } from '../Builder/EntitySelection/types';
import {
  MAX_BREAKDOWN_COHORTS,
  MAX_GLOBAL_COHORTS,
  MAX_STEP_COUNT,
} from '../../config/constants';
import {
  builderSelectedOptionsSelector,
  dataTableMeasuresSelector,
  defaultLogOpsSelector,
  measuresSelector,
  stepsSelector,
} from '../../redux/selectors/builder';
import { parseMeasureToCohortRow } from '../../utils/builder/measures/parseMeasureToCohortRow';
import {
  EntityDropzoneDataTypeEnum,
  IEntityDropzoneDataProperties,
  IEventEntityDropzoneDataType,
  IFieldEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from './validation';
import { isEmptyEventConditions } from '../../utils/builder';
import { EntityDropzoneType } from '../QuickAccessDictionary/DictionaryTabs/types';
import toastMessage from '../../utils/toastMessage';

interface ISelectEntityProperties {
  node: IEntityNode;
  data: IEntityDropzoneDataProperties;
  setDefaultFilter: React.Dispatch<
    React.SetStateAction<IFilterFieldsFragment | null>
  >;
  setPopoverId: React.Dispatch<React.SetStateAction<string | null>>;
}

const useDndEntityHandler = (schemaId: string) => {
  const dispatch = useAppDispatch();
  const measures = useAppSelector(measuresSelector);
  const dataTableMeasures = useAppSelector(dataTableMeasuresSelector);
  const steps = useAppSelector(stepsSelector);
  const defaultLogOps = useAppSelector(defaultLogOpsSelector);
  const measureFunctions = useAppSelector(
    ({ builder }) => builder.defaultMeasureFunctions,
  );
  const selectedOptions = useAppSelector(builderSelectedOptionsSelector);
  const { getEntityFromCacheOrFetch } = useEntityLoader(schemaId);

  const cohortMeasureFunctions = useAppSelector(
    ({ builder }) => builder.cohortConfig.measureConfig.functions,
  );

  const andLogicOp = defaultLogOps.find(
    (lop) => lop.key === LogicalOperatorKey.And,
  ) as ILogicalOperator;

  const handleFieldEntities = useCallback(
    ({
      entity,
      data,
      setDefaultFilter,
      setPopoverId,
    }: {
      entity: IFieldFieldsFragment;
      data: IFieldEntityDropzoneDataType;
      setDefaultFilter: React.Dispatch<
        React.SetStateAction<IFilterFieldsFragment | null>
      >;
      setPopoverId: React.Dispatch<React.SetStateAction<string | null>>;
    }) => {
      // if filter is passed in, modify the filter object to include the new entity
      if (data.filter && !isNil(data.conditionIndex)) {
        const newFilter = updateFilterWithCondition({
          filter: data.filter,
          conditionIndex: data.conditionIndex,
          predicateIndex: data.predicateIndex,
          entity,
          relOp: entity.relOps[0],
          logicOp: andLogicOp,
        });
        setDefaultFilter(newFilter);
      } else {
        // otherwise create a new filter with the entity
        const newFilter = createNewBasicFilter({
          entity,
          relOp: entity.relOps[0],
        });

        setDefaultFilter(newFilter);
      }

      if (data.rowMeta) {
        setPopoverId(
          createDictionaryEntityDropzoneId(
            EntityDropzoneType.Fields,
            data.rowMeta,
          ),
        );
      }
    },
    [andLogicOp],
  );

  const selectEventHandler = useCallback(
    ({
      data,
      entity,
    }: {
      data: IEventEntityDropzoneDataType;
      entity: IBasicEventValueFieldsFragment | IVirtualEventFieldsFragment;
    }) => {
      batch(() => {
        // if applying new event to segment drop area, add a new segment row first
        // before populating the event
        if (data.addCohortRow) {
          if (data.rowMeta.rowType === RowType.breakdownCohorts) {
            dispatch(
              addCohortRow({
                rowType: RowType.breakdownCohorts,
                limit: MAX_BREAKDOWN_COHORTS,
              }),
            );
          }

          if (data.rowMeta.rowType === RowType.globalCohorts) {
            dispatch(
              addCohortRow({
                rowType: RowType.globalCohorts,
                limit: MAX_GLOBAL_COHORTS,
              }),
            );
          }

          if (data.rowMeta.rowType === RowType.builderCohorts) {
            dispatch(addSimpleCohort(data.rowMeta));
          }
        }

        // special case for funnel steps
        if (data.rowMeta.rowType === RowType.steps) {
          // Step auto add if last step is not empty and current action is adding event from 0 to 1
          const isLastStepRowEmpty = isEmptyEventConditions(
            steps[steps.length - 1].eventConditions,
          );

          if (
            (!isLastStepRowEmpty ||
              steps.length - 1 === data.rowMeta.rowIndex) &&
            steps.length < MAX_STEP_COUNT
          ) {
            dispatch(
              addNewRowAction({
                rowMeta: { rowType: RowType.steps, rowIndex: steps.length - 1 },
              }),
            );
          }
        }

        if (data.isNewRow) {
          if (data.rowMeta.rowType === RowType.mergeEvents) {
            dispatch(addMergeEventAction());
          } else if (data.rowMeta.rowType === RowType.foldEvents) {
            dispatch(addFoldEventAction());
          } else {
            dispatch(addNewRowAction({ rowMeta: data.rowMeta }));
          }
        }

        if (data.isNewEvent) {
          dispatch(addNewEventAction(data.rowMeta));
        }

        dispatch(
          addEventValuesAction({
            rowMeta: data.rowMeta,
            newEvents: [...(data.existingEvents ?? []), entity],
          }),
        );

        // if there are no measure by functions selected when dropping a new event
        // default the measure by function to unique users.
        if (data.shouldAddMeasureFunction) {
          const newFunction = getDndMeasureFunction(
            // temporary usage of both cohort & defaultMeasureFunctions, kobol needs to make an update
            // to make this more accessible.
            measureFunctions.length ? measureFunctions : cohortMeasureFunctions,
            selectedOptions && 'subject' in selectedOptions
              ? selectedOptions.subject ?? null
              : null,
          );
          dispatch(
            setBuilderFunctionAction({
              rowMeta: data.rowMeta,
              newFunction,
            }),
          );
        }
      });
    },
    [
      dispatch,
      steps,
      measureFunctions,
      cohortMeasureFunctions,
      selectedOptions,
    ],
  );

  const selectMeasureHandler = useCallback(
    ({
      data,
      entity,
    }: {
      data: IMeasureEntityDropzoneDataType;
      entity: ISimpleMeasureFieldsFragment | ICompoundMeasureFieldsFragment;
    }) => {
      const { rowMeta } = data;
      batch(() => {
        // adding measure to cohorts
        if (
          rowMeta.rowType === RowType.breakdownCohorts ||
          rowMeta.rowType === RowType.builderCohorts ||
          rowMeta.rowType === RowType.globalCohorts ||
          rowMeta.rowType === RowType.predictionCohorts
        ) {
          // add a new cohort row to the cohort type & switch to measure cohort type
          if (data.addCohortRow) {
            if (rowMeta.rowType === RowType.globalCohorts) {
              dispatch(
                addCohortRow({
                  rowType: RowType.globalCohorts,
                  limit: MAX_GLOBAL_COHORTS,
                }),
              );
            }
            if (rowMeta.rowType === RowType.breakdownCohorts) {
              dispatch(
                addCohortRow({
                  rowType: RowType.breakdownCohorts,
                  limit: MAX_BREAKDOWN_COHORTS,
                }),
              );
            }

            if (rowMeta.rowType === RowType.builderCohorts) {
              dispatch(addSimpleCohort(rowMeta));
            }
          }

          // change cohort type to measure
          dispatch(
            setCohortRowSimpleTypeKey({
              value: SimpleCohortTypeKey.Measure,
              rowMeta,
            }),
          );

          // set the measure
          dispatch(
            setRowPropAction({
              rowMeta,
              row: parseMeasureToCohortRow(entity),
            }),
          );
        }

        // adding measure to measure rows.
        if (rowMeta.rowType === RowType.measures) {
          // if the rowIndex is greater than the number of measures, add a new row for a new measure
          if (rowMeta.rowIndex >= measures.length) {
            dispatch(addNewRowAction({ rowMeta }));
          }

          // special case for compound measures
          if (
            entity.__typename === 'CompoundMeasure' &&
            !rowMeta.cohortRowType
          ) {
            const someEventConditionsFilled = measures.some((measure) => {
              return !isEmptyEventConditions(measure.eventConditions);
            });

            // if any of the measures have event conditions filled already,
            // do not allow loading a compound measure
            if (someEventConditionsFilled) {
              return;
            }
          }

          // handling adding measures to measure cohort filter
          if (rowMeta.cohortRowType === 'cohortFilter') {
            // change cohort type to measure
            dispatch(
              setCohortRowSimpleTypeKey({
                value: SimpleCohortTypeKey.Measure,
                rowMeta,
              }),
            );

            // set the measure
            dispatch(
              setRowPropAction({
                rowMeta,
                row: parseMeasureToCohortRow(entity),
              }),
            );
            return;
          }

          dispatch(queryMeasureSetNamedMeasure({ measure: entity, rowMeta }));
        }

        // adding measure to data table measures
        if (rowMeta.rowType === RowType.dataTableMeasures) {
          // if the rowIndex is greater than the number of dataTableMeasures, add a new row for a new measure
          if (rowMeta.rowIndex >= dataTableMeasures.length) {
            dispatch(addNewRowAction({ rowMeta }));
          }

          // handling adding measures to measure cohort filter
          if (rowMeta.cohortRowType === 'cohortFilter') {
            // change cohort type to measure
            dispatch(
              setCohortRowSimpleTypeKey({
                value: SimpleCohortTypeKey.Measure,
                rowMeta,
              }),
            );

            // set the measure
            dispatch(
              setRowPropAction({
                rowMeta,
                row: parseMeasureToCohortRow(entity),
              }),
            );
            return;
          }

          dispatch(
            dataTableMeasureSetNamedMeasure({ measure: entity, rowMeta }),
          );
        }
      });
    },
    [dispatch, dataTableMeasures, measures],
  );

  const selectEntity = useCallback(
    async ({
      node,
      data,
      setDefaultFilter,
      setPopoverId,
    }: ISelectEntityProperties) => {
      const entity = await getEntityFromCacheOrFetch(node);

      // clear builder errors before dropping.
      if ('rowMeta' in data) {
        if (data.errorKey) {
          dispatch(
            removeErrorAction({
              rowMeta: data.rowMeta,
              errorKey: data.errorKey,
            }),
          );
        }

        dispatch(
          removeErrorAction({
            rowMeta: data.rowMeta,
            errorKey: '',
            clearRow: true,
          }),
        );
      }

      if (data.type === EntityDropzoneDataTypeEnum.Events) {
        if (
          data.rowMeta &&
          (entity.__typename === 'BasicEventValue' ||
            entity.__typename === 'VirtualEvent')
        ) {
          if (
            data.rowMeta.rowType === RowType.expandEvents &&
            entity.__typename === 'BasicEventValue'
          ) {
            if (data.isNewRow) {
              dispatch(addExpandEventAction());
            }
            dispatch(
              setExpandEventAction({
                rowMeta: data.rowMeta,
                expandEvent: {
                  field: null,
                  event: entity,
                },
              }),
            );

            setPopoverId(
              createDictionaryEntityDropzoneId(
                EntityDropzoneType.Events,
                data.rowMeta,
              ),
            );
            return;
          }

          // check if event already exists in existingEvents
          const existingEvent = checkExistingEvent(data.existingEvents, entity);
          const isSameEventType = checkExistingEventsEntityType(
            data.existingEvents,
            entity,
          );

          // only add the event if it does not exist yet
          if (!existingEvent && isSameEventType) {
            selectEventHandler({
              data,
              entity,
            });
          }
        }
      }

      if (
        data.type === EntityDropzoneDataTypeEnum.Measures &&
        (entity.__typename === 'SimpleMeasure' ||
          entity.__typename === 'CompoundMeasure')
      ) {
        selectMeasureHandler({
          data,
          entity,
        });
      }

      if (
        data.type === EntityDropzoneDataTypeEnum.Cohorts &&
        (entity.__typename === 'RetentionCohort' ||
          entity.__typename === 'FunnelCohort' ||
          entity.__typename === 'PerformedCohort' ||
          entity.__typename === 'CompoundCohort' ||
          entity.__typename === 'ExpressionCohort' ||
          entity.__typename === 'MeasureCohort')
      ) {
        if (data.rowMeta.rowType === RowType.builderCohorts) {
          if (data.addCohortRow) {
            dispatch(addSimpleCohort(data.rowMeta));
          }

          dispatch(
            populateNamedBuilderCohort({
              cohort: entity,
              rowMeta: data.rowMeta,
            }),
          );

          return;
        }

        dispatch(
          populateNamedCohortReference({
            cohort: entity,
            rowMeta: data.rowMeta,
          }),
        );
      }

      if (data.type === EntityDropzoneDataTypeEnum.Significance) {
        if (
          entity.__typename === 'RetentionCohort' ||
          entity.__typename === 'FunnelCohort' ||
          entity.__typename === 'PerformedCohort' ||
          entity.__typename === 'CompoundCohort' ||
          entity.__typename === 'ExpressionCohort' ||
          entity.__typename === 'MeasureCohort'
        ) {
          if (!isNil(data.variantIndex)) {
            dispatch(
              setSignificanceVariant({
                modeKey: SignificanceModeKey.Segment,
                variant: entity,
                variantIndex: data.variantIndex,
              }),
            );
            return;
          }

          dispatch(
            setSignificanceControlGroup({
              modeKey: SignificanceModeKey.Segment,
              controlGroup: entity,
            }),
          );
          return;
        }

        if (entity.__typename === 'Field') {
          dispatch(setSignificanceBreakdownField({ newField: entity }));
        }

        if (
          entity.__typename === 'SimpleMeasure' ||
          entity.__typename === 'CompoundMeasure'
        ) {
          dispatch(setSignificanceMeasureMetric({ namedMeasure: entity }));
        }
      }

      if (
        data.type === EntityDropzoneDataTypeEnum.Filters &&
        entity.__typename === 'NamedFilter'
      ) {
        dispatch(
          setFilterAction({
            rowMeta: data.rowMeta,
            newFilter: entity,
          }),
        );
      }

      // do not allow nested fields
      if (entity.__typename === '__NestedFieldEntity') {
        toastMessage({
          message: `Dragging a Nested Field is not supported at the moment.`,
          type: 'info',
        });
        return;
      }

      if (entity.__typename === 'Field') {
        if (data.type === EntityDropzoneDataTypeEnum.Breakdowns) {
          batch(() => {
            // if groupLimit is passed in, create a new breakdown for the breakdown group
            if (!isNil(data.groupLimit)) {
              dispatch(addBreakdownGroup({ groupLimit: data.groupLimit }));
            }

            // if groupIndex is passed in, add a field to the breakdown at groupIndex
            if (!isNil(data.groupIndex)) {
              dispatch(
                setBreakdownGroupField({
                  groupIndex: data.groupIndex,
                  field: entity,
                }),
              );
            }
          });
        }

        if (data.type === EntityDropzoneDataTypeEnum.Fields) {
          handleFieldEntities({
            entity,
            data,
            setDefaultFilter,
            setPopoverId,
          });
        }

        if (
          data.type === EntityDropzoneDataTypeEnum.Property &&
          entity.__typename === 'Field'
        ) {
          dispatch(
            setEventPropertyAction({
              rowMeta: data.rowMeta,
              property: entity,
            }),
          );
        }
      }
    },
    [
      dispatch,
      getEntityFromCacheOrFetch,
      handleFieldEntities,
      selectEventHandler,
      selectMeasureHandler,
    ],
  );

  return { selectEntity };
};

export default useDndEntityHandler;
